import { Injectable } from '@angular/core';
import type { AnomalySeries, ILaneConfig, LaneConfig } from 'commons';
import type { MarkAreaComponentOption, YAXisComponentOption } from 'echarts';
import type { KPI_Table } from '../../dtis-multilane-viewer/KPI_Table';
import { buildLabeledTimeRangeMarkArea } from './mark-area-builder.util';

interface MarkAreaSeries {
  xAxisIndex?: number;
  markArea?: MarkAreaComponentOption;
  tooltip?: unknown;
}

export interface AnomalyInterval {
  readonly startTime: Date;
  readonly endTime: Date;
  readonly phaseName: string;
  readonly borderColor: string;
  readonly backgroundColor: string;
  readonly labelColor: string;
  readonly identifier: string;
  readonly metaData?: Record<string, string | number | undefined>;
}

export interface AnomalyLaneIntervals {
  readonly xAxisIndex: number;
  readonly intervals: AnomalyInterval[];
}

interface AnomalyMarkAreaResult {
  readonly markArea: MarkAreaComponentOption;
  readonly laneIndex: number;
  readonly xAxisIndex: number;
  readonly hasData: boolean;
  readonly intervals: AnomalyInterval[];
}

/**
 * Service for building anomaly mark areas for charts.
 * Anomalies are rendered as time-based ECharts mark areas on their matching lane.
 */
@Injectable({ providedIn: 'root' })
export class KpiSeriesBuilderService {
  /**
   * Injects anomaly mark areas into the existing series array.
   * Returns the anomaly intervals per lane so the global tooltip formatter can render their metadata.
   */
  injectKpiSeries(kpiTable: KPI_Table, lanes: LaneConfig[] | ILaneConfig[], yAxis: YAXisComponentOption[], existingSeries: unknown[]): AnomalyLaneIntervals[] {
    if (!kpiTable || Object.keys(kpiTable).length === 0) {
      return [];
    }

    const results = this.buildAnomalyMarkAreas(kpiTable, lanes, yAxis);

    for (const result of results) {
      if (!result.hasData) {
        continue;
      }

      const targetSeries = existingSeries.find(
        (series): series is MarkAreaSeries =>
          !!series && typeof series === 'object' && 'xAxisIndex' in series && (series as MarkAreaSeries).xAxisIndex === result.xAxisIndex
      );
      if (!targetSeries) {
        continue;
      }

      const existingMarkArea = targetSeries.markArea;
      if (existingMarkArea?.data?.length) {
        targetSeries.markArea = {
          ...existingMarkArea,
          data: [...(result.markArea.data ?? []), ...existingMarkArea.data],
        };
      } else {
        targetSeries.markArea = result.markArea;
      }

      targetSeries.tooltip = {
        trigger: 'axis',
        formatter: (params: Array<{ value?: unknown[]; data?: unknown[] }>) => {
          const firstParam = params[0] as { value?: unknown[]; data?: unknown[] } | undefined;
          const firstValue = Array.isArray(firstParam?.value) ? firstParam.value[0] : firstParam?.value;
          const firstData = Array.isArray(firstParam?.data) ? firstParam.data[0] : firstParam?.data;
          const rawTime = firstValue ?? firstData ?? 0;
          const hoverTime = rawTime instanceof Date ? rawTime : new Date(String(rawTime));

          const activeInterval = result.intervals.find((interval) => hoverTime >= interval.startTime && hoverTime <= interval.endTime);
          if (!activeInterval?.metaData || Object.keys(activeInterval.metaData).length === 0) {
            return '';
          }

          const rows = Object.entries(activeInterval.metaData)
            .filter(([, value]) => value !== undefined && value !== null)
            .map(([key, value]) => `<div class="flex gap-2 justify-between"><span>${key}</span><span>${String(value)}</span></div>`)
            .join('');

          return `<div class="flex flex-col p-2 gap-2"><div class="font-semibold">Anomaly</div>${rows}</div>`;
        },
      };
    }

    return results.filter((result) => result.hasData).map((result) => ({ xAxisIndex: result.xAxisIndex, intervals: result.intervals }));
  }

  /**
   * Builds anomaly mark areas for all lanes from KPI/anomaly table data.
   */
  private buildAnomalyMarkAreas(kpiTable: KPI_Table, lanes: LaneConfig[] | ILaneConfig[], yAxis: YAXisComponentOption[]): AnomalyMarkAreaResult[] {
    const flattenedAnomalies = Object.values(kpiTable)
      .flat()
      .filter((item): item is AnomalySeries => !!item && !!item.startTimestamp && !!item.endTimestamp);

    if (flattenedAnomalies.length === 0) {
      return [];
    }

    const results: AnomalyMarkAreaResult[] = [];

    for (let laneIndex = 0; laneIndex < lanes.length; laneIndex += 1) {
      const xAxisIndex = this.findXAxisIndex(lanes, laneIndex);
      const laneAnomalies = flattenedAnomalies.filter((item) => this.matchesLane(item, lanes[laneIndex]));
      const intervals = this.anomaliesToIntervals(laneAnomalies);
      const markArea = buildLabeledTimeRangeMarkArea(intervals);

      results.push({
        markArea,
        laneIndex,
        xAxisIndex,
        hasData: intervals.length > 0,
        intervals,
      });
    }

    return results.filter((result) => result.hasData && yAxis.some((axis) => axis.gridIndex === result.xAxisIndex));
  }

  private matchesLane(item: AnomalySeries, lane: LaneConfig | ILaneConfig): boolean {
    return lane?.controls?.some(({ id }) => id === item.channelIdentifier);
  }

  private anomaliesToIntervals(anomalies: AnomalySeries[]): AnomalyInterval[] {
    return anomalies.map((anomaly) => ({
      startTime: new Date(anomaly.startTimestamp),
      endTime: new Date(anomaly.endTimestamp),
      phaseName: anomaly.testPhase || 'Anomaly',
      borderColor: '#E62C27',
      backgroundColor: 'rgba(230, 44, 39, 0.16)',
      labelColor: '#7A1F1F',
      identifier: `${anomaly.channelIdentifier ?? 'anomaly'}-${anomaly.startTimestamp}-${anomaly.endTimestamp}`,
      metaData: anomaly.metaData ?? anomaly.metadata ?? {},
    }));
  }

  private findXAxisIndex(lanes: Array<LaneConfig | ILaneConfig>, laneIndex: number): number {
    return Math.max(
      lanes.findIndex((lane, index) => index === laneIndex && lane.controls?.length > 0),
      0
    );
  }
}
